import { db } from '../services/db';
import { UrlParams } from './urlUtils';

/**
 * Interface for voice command processing results
 */
export interface VoiceCommandResult extends UrlParams {
  confidence: number;
  needsConfirmation: boolean;
  originalText: string;
}

/**
 * Processes a natural language voice command and extracts relevant parameters
 * @param text The raw text from the voice command
 * @returns Processed parameters with confidence scores
 */
export const processVoiceCommand = async (text: string): Promise<VoiceCommandResult> => {
  const normalizedText = text
    .replace(/[.,;:!?]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const result: VoiceCommandResult = {
    confidence: 0,
    needsConfirmation: true,
    originalText: text
  };

  // Extract action (clock in, clock out, switch)
  const actionPatterns = [
    { regex: /\b(?:clock|clocked)\s+(?:in|into)\b/i, action: 'clock-in' as const },
    { regex: /\b(?:clock|clocked)\s+out\b/i, action: 'clock-out' as const },
    { regex: /\bswitch\b/i, action: 'switch' as const }
  ];

  for (const pattern of actionPatterns) {
    if (pattern.regex.test(normalizedText)) {
      result.action = pattern.action;
      result.confidence += 0.2;
      break;
    }
  }

  // Extract project
  const projectMatch = normalizedText.match(/\b(?:to|on|for|at|project)\s+(?:project\s+)?([a-zA-Z0-9 &'/-]+?)(?=\s+(?:task|at|with|notes)\b|$)/i);
  if (projectMatch && projectMatch[1]) {
    const potentialProject = projectMatch[1].trim();
    
    // Try to find matching project in database
    const projects = await db.projects.toArray();
    const exactMatch = projects.find(p => 
      p.name.toLowerCase() === potentialProject.toLowerCase()
    );
    
    if (exactMatch) {
      result.project = exactMatch.name;
      result.confidence += 0.2;
    } else {
      // Look for fuzzy matches
      const fuzzyMatches = projects.map(p => ({
        name: p.name,
        score: calculateSimilarity(p.name.toLowerCase(), potentialProject.toLowerCase())
      })).filter(match => match.score > 0.7);
      
      if (fuzzyMatches.length > 0) {
        // Sort by similarity score
        fuzzyMatches.sort((a, b) => b.score - a.score);
        result.project = fuzzyMatches[0].name;
        result.confidence += 0.1 + (fuzzyMatches[0].score * 0.1); // Adjust confidence based on similarity
      } else {
        // No good match, use the extracted text as a new project
        result.project = potentialProject;
        result.confidence += 0.05;
      }
    }
  }

  // Extract task
  const taskMatch = normalizedText.match(/\btask\s+([a-zA-Z0-9 &'/-]+?)(?=\s+(?:at|with|notes)\b|$)/i);
  if (taskMatch && taskMatch[1]) {
    result.task = taskMatch[1].trim();
    result.confidence += 0.15;
    
    // If we have a project, check if this task belongs to it
    if (result.project) {
      const projects = await db.projects.where('name').equals(result.project).toArray();
      if (projects.length > 0 && projects[0].tasks.some(t => 
        calculateSimilarity(t.toLowerCase(), result.task!.toLowerCase()) > 0.8
      )) {
        result.confidence += 0.1;
      }
    }
  }

  // Extract location
  const locationMatch = normalizedText.match(/\b(?:at|in)\s+(site|shop)\b/i);
  if (locationMatch && locationMatch[1]) {
    result.location = locationMatch[1].charAt(0).toUpperCase() + locationMatch[1].slice(1).toLowerCase();
    result.confidence += 0.15;
  }

  // Extract notes
  const notesMatch = normalizedText.match(/\b(?:with|notes)\s+(.+?)$/i);
  if (notesMatch && notesMatch[1]) {
    result.notes = notesMatch[1].trim();
    result.confidence += 0.1;
  }

  // Determine if confirmation is needed
  const actionRequiresProject = result.action === 'switch';
  result.needsConfirmation =
    result.confidence < 0.7 ||
    !result.action ||
    (actionRequiresProject && !result.project);

  return result;
};

/**
 * Calculate string similarity using Levenshtein distance
 * @param str1 First string
 * @param str2 Second string
 * @returns Similarity score between 0 and 1
 */
export const calculateSimilarity = (str1: string, str2: string): number => {
  const normalizedStr1 = str1.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const normalizedStr2 = str2.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const len1 = normalizedStr1.length;
  const len2 = normalizedStr2.length;
  
  // If either string is empty, the distance is the length of the other string
  if (len1 === 0) return 0;
  if (len2 === 0) return 0;
  
  // Create a matrix of size (len1+1) x (len2+1)
  const matrix: number[][] = Array(len1 + 1).fill(null).map(() => Array(len2 + 1).fill(null));
  
  // Initialize the first row and column
  for (let i = 0; i <= len1; i++) matrix[i][0] = i;
  for (let j = 0; j <= len2; j++) matrix[0][j] = j;
  
  // Fill the matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = normalizedStr1[i - 1] === normalizedStr2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1, // deletion
        matrix[i][j - 1] + 1, // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }
  
  // Calculate similarity as 1 - normalized distance
  const maxLen = Math.max(len1, len2);
  return 1 - (matrix[len1][len2] / maxLen);
};
